import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Auth } from "aws-amplify";
import {
  Button,
  FormControl,
  Spinner,
  FormGroup,
  Toast,
} from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { PHONE_COUNTRY_CODES } from "../constants";
import RippleButton from "./Onboarding/RippleButton";

const validAuthStates = ["newPasswordRequired"];
const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, "*Password must have at least 8 characters")
    .max(50, "*Password can't be longer than 50 characters")
    .required("*Password is required"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password")], "*Password does not match")
    .min(8, "*Password must have at least 8 characters")
    .max(50, "*Password can't be longer than 50 characters")
    .required("*Password is required"),
  phoneNumber: Yup.string()
    .required("*Phone number Required")
    .min(6, "*Phone number must have at least 6 characters")
    .max(15, "*Phone number can't be longer than 15 characters"),
});

const CustomNewPasswordRequired = ({ authState, onStateChange, account }) => {
  const [showToast, setShowToast] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    isProcessing: false,
    errorMsg: "",
  });
  const [phoneCode, setPhoneCode] = useState(PHONE_COUNTRY_CODES[0]);

  useEffect(() => {
    // Reset status of reset-reset form
    setSubmitStatus({ isProcessing: false, errorMsg: "" });
  }, [authState]);

  const toggleToast = () => {
    setShowToast(!showToast);
  };

  const newPasswordSubmit = async (values) => {
    const { password, phoneNumber } = values;
    const composePhoneNumber = `${phoneCode || "+1"}${phoneNumber.replace(
      /[-()]/g,
      ""
    )}`;

    try {
      setSubmitStatus({
        isProcessing: true,
        errorMsg: "",
      });

      const cognitoUser = await Auth.signIn(account.email, account.password);
      await Auth.completeNewPassword(cognitoUser, password, {
        email: account.email,
        phone_number: composePhoneNumber,
      });

      setSubmitStatus({
        isProcessing: false,
        errorMsg: "",
      });

      toggleToast();
    } catch (e) {
      setSubmitStatus({
        isProcessing: false,
        errorMsg:
          e.message || "Fail to update your password. Please try again!",
      });
    }
  };

  const onChangePhoneCode = (e) => {
    setPhoneCode(e.target.value);
  };

  const changeToSigninForm = () => {
    onStateChange("signIn");
  };

  return (
    <>
      <Toast show={showToast} onClose={toggleToast} autohide>
        <Toast.Body>Your password was updated successfully.</Toast.Body>
      </Toast>
      {validAuthStates.includes(authState) ? (
        <Formik
          enableReinitialize
          initialValues={{
            email: account.email,
            password: "",
            confirmPassword: "",
            phoneNumber: "",
          }}
          validationSchema={validationSchema}
          onSubmit={newPasswordSubmit}
        >
          {({ errors, touched }) => (
            <Form
              className="auth-form auth-new-password-form"
              autoComplete="off"
            >
              <FormGroup controlId="account" className="onboarding-form-group">
                <FormControl
                  type="text"
                  value={account.email}
                  disabled
                  className="onboarding-input"
                />
              </FormGroup>
              <Field name="phoneNumber">
                {({ field }) => (
                  <div className="auth-new-phone-number">
                    <div className="select-phone-code-wrapper">
                      <select
                        name="phone_code"
                        className="select-phone-code"
                        defaultValue={PHONE_COUNTRY_CODES[0]}
                        onChange={onChangePhoneCode}
                        data-testid="select-phone-code"
                      >
                        {PHONE_COUNTRY_CODES.map((phoneCode) => (
                          <option key={phoneCode} value={phoneCode}>
                            {phoneCode}
                          </option>
                        ))}
                      </select>
                    </div>
                    <FormGroup
                      controlId="phoneNumber"
                      className="onboarding-form-group"
                    >
                      <FormControl
                        type="tel"
                        placeholder="Phone number"
                        onChange={field.onChange}
                        value={field.value}
                        data-prop="phoneNumber"
                        className="onboarding-input"
                        isValid={touched.phoneNumber && !errors.phoneNumber}
                        isInvalid={touched.phoneNumber && errors.phoneNumber}
                        autoComplete="new-phone"
                        data-testid="new-phone"
                      />
                      <FormControl.Feedback type="invalid">
                        {errors.phoneNumber}
                      </FormControl.Feedback>
                    </FormGroup>
                  </div>
                )}
              </Field>
              <Field name="password">
                {({ field }) => (
                  <FormGroup
                    controlId="password"
                    className="onboarding-form-group"
                  >
                    <FormControl
                      type="password"
                      placeholder="New password"
                      value={field.value}
                      onChange={field.onChange}
                      data-prop={"password"}
                      className="onboarding-input"
                      isValid={touched.password && !errors.password}
                      isInvalid={touched.password && errors.password}
                      autoComplete="new-password"
                      data-testid="new-password"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.password}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>
              <Field name="confirmPassword">
                {({ field }) => (
                  <FormGroup
                    controlId="confirmPassword"
                    className="onboarding-form-group"
                  >
                    <FormControl
                      type="password"
                      placeholder="Confirm password"
                      value={field.value}
                      onChange={field.onChange}
                      data-prop="confirmPassword"
                      className="onboarding-input"
                      isValid={
                        touched.confirmPassword && !errors.confirmPassword
                      }
                      isInvalid={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      autoComplete="new-confirm-password"
                      data-testid="new-confirm-password"
                    />
                    <FormControl.Feedback type="invalid">
                      {errors.confirmPassword}
                    </FormControl.Feedback>
                  </FormGroup>
                )}
              </Field>

              <div>
                <RippleButton
                  type
                  disabled={submitStatus.isProcessing}
                  customStyles="w-75"
                >
                  Submit
                  {submitStatus.isProcessing && (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="isProcessing"
                      className="ml-1"
                      aria-hidden="true"
                    />
                  )}
                </RippleButton>
                {submitStatus.errorMsg && (
                  <p className={"mt-2 mb-0 error-message"}>
                    {submitStatus.errorMsg}
                  </p>
                )}

                <Button
                  variant="link"
                  className="auth-navigate"
                  onClick={changeToSigninForm}
                  data-testid="login-to-account"
                >
                  Login to your account
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      ) : null}
    </>
  );
};

CustomNewPasswordRequired.propTypes = {
  authState: PropTypes.string,
  onStateChange: PropTypes.func,
  account: PropTypes.object.isRequired,
};

export default CustomNewPasswordRequired;
